import { Modal } from "@fluentui/react";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body2,
  Button,
  Card,
  CardHeader,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Input,
  Link,
  Persona,
  Tag,
  Text,
  Tooltip,
  useId,
} from "@fluentui/react-components";
import {
  Checkmark16Regular,
  Clipboard16Filled,
  Clipboard16Regular,
  Dismiss20Regular,
  Dismiss24Regular,
  Edit16Filled,
  Edit16Regular,
  Eraser20Regular,
} from "@fluentui/react-icons";
import { ConstantValues } from "common/constants";
import { AIModelSettings } from "components/aiModelSettings/AIModelSettings";
import { CustomDialog } from "components/customDialog/CustomDialog";
import { TagWithAttachments } from "components/tagWithAttachments/TagWithAttachments";
import { LLMType } from "models/enums/LLMType";
import { FileWithId } from "models/FileWithId";
import { IStandardChartData } from "models/IChartData";
import { ICitation } from "models/ICitation";
import { IConversation } from "models/IConversation";
import { IFile } from "models/IFile";
import { IMessage } from "models/IMessage";
import { IAIResponseError } from "models/IResponseError";
import { ITag } from "models/ITag";
import { IUser } from "models/IUser";
import { IWorkspace } from "models/IWorkspace";
import { Fragment, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { downloadFile, uploadFilesToStorage } from "utils/blobUtils";
import {
  buildCitationKeyMap,
  copyTextToClipboard,
  getTimeAndDate,
  renderContentWithCitations,
} from "utils/generalUtils";
import { v4 as uuidv4 } from "uuid";
import {
  addMessageToConversation,
  getAIResponse,
  getPrompts,
  updateContentOfConversation,
} from "../../../api";
import { resolveAsset } from "../../../common/fileIcons";
import { getChunkAndUpdateAIMessage } from "../../../common/streamProcessing.js";
import { setCurrentFiles } from "../../../redux/files";
import { setNewConfigForKey } from "../../../redux/llmConfig";
import { setCurrentMessages } from "../../../redux/messages";
import { setCurrentPrompt } from "../../../redux/selectedPrompt";
import { setTagsForKey } from "../../../redux/tags";
import {
  setInitialMessage,
  setTemporaryChatOpen,
} from "../../../redux/temporaryChat";
import { CategoryTagTree } from "components/categoryTagTree/CategoryTagTree";
import { DocumentCopyIcon } from "icons/DocumentCopyIcon";
import { AddPrompt } from "../addPrompt/AddPrompt";
import { FilePreview } from "../filePreview/FilePreview";
import { FileUploader } from "../fileUploader/FileUploader";
import { MessageInput } from "../messageInput/MessageInput";
import { useResizer } from "hooks/useResizer";
import { useStyles } from "./TemporaryChat.styles";

export const TemporaryChat = (): JSX.Element => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const isMobile = useResizer();

  const currentUser: IUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const currentlySelectedConversation: IConversation = useSelector(
    (state: RootState) => state.currentConversationSetter.value,
  );
  const realChatMessages: IMessage[] = useSelector(
    (state: RootState) => state.currentMessagesSetter.value,
  );
  const currentTags: ITag[] = useSelector(
    (state: RootState) =>
      state.currentTagsSlice.tags[ConstantValues.TEMPORARY_CHAT] || [],
  );
  const currentLLMConfig: any = useSelector(
    (state: RootState) =>
      state.currentLLMConfigSlice.config[ConstantValues.TEMPORARY_CHAT] ?? {
        llmType: LLMType.CLAUDE,
        rag: false,
        citate: false,
      },
  );
  const initialMessage: any = useSelector(
    (state: RootState) => state.temporaryChatSlice.initialMessage,
  );
  const workspaces: IWorkspace[] = useSelector(
    (state: RootState) => state.workspacesSlice.value,
  );
  const activeWorkspace: IWorkspace | null = useSelector(
    (state: RootState) => state.activeWorkspaceSlice.value,
  );
  const currentPrompt = useSelector(
    (state: RootState) =>
      state.currentPromptSlice.prompts[ConstantValues.TEMPORARY_CHAT],
  );

  const [temporaryMessages, setTemporaryMessages] = useState<IMessage[]>([]);
  const [text, setText] = useState<string>(ConstantValues.EMPTY_STRING);
  const [aiGenerateMessage, setAiGenerateMessage] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [uploadFiles, setUploadFiles] = useState<FileWithId[]>([]);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [injectedMessageId, setInjectedMessageId] = useState<string | null>(
    null,
  );
  const [editMessageId, setEditMessageId] = useState<string | null>(null);
  const [editMessageValue, setEditMessageValue] = useState<string | null>(null);
  const [cleanUpOnClose, setCleanUpOnClose] = useState<boolean>(true);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState<boolean>(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [unsupportedFileMessage, setUnsupportedFileMessage] = useState<
    string | null
  >(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const { data: prompts } = useQuery(
    "prompts",
    async () => {
      const prompts = await getPrompts();
      return prompts;
    },
    { refetchOnWindowFocus: false },
  );

  const editMessageInputId: string = useId("input");
  const messagesEndRef: React.MutableRefObject<HTMLDivElement | null> =
    useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect((): void => {
    if (temporaryMessages.length % 2 === 1 && autoScrollEnabled) {
      scrollToBottom();
    }
  }, [temporaryMessages, aiGenerateMessage, currentlySelectedConversation?.id]);

  useEffect((): void => {
    if (temporaryMessages.length % 2 !== 0) {
      handleUserMessage();
    }
  }, [temporaryMessages]);

  useEffect(() => {
    const container: HTMLDivElement | null = messagesContainerRef.current;

    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (initialMessage) {
      setTemporaryMessages([initialMessage]);
    }

    return () => {
      if (initialMessage && cleanUpOnClose === true) {
        const reUpdateMessages = [];
        for (const m of realChatMessages.slice()) {
          if (m?.id === initialMessage?.id) {
            reUpdateMessages.push({
              ...m,
              content: initialMessage?.initialContent,
            });
          } else reUpdateMessages.push(m);
        }
        dispatch(setCurrentMessages(reUpdateMessages));
      }
    };
  }, []);

  useEffect((): void => {
    if (currentPrompt) {
      setText(currentPrompt?.description);
    }
  }, [currentPrompt?.id]);

  const handleScroll = (): void => {
    if (!messagesContainerRef.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } =
      messagesContainerRef.current;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      setAutoScrollEnabled(true);
      return;
    }

    setAutoScrollEnabled(false);
  };

  const scrollToBottom = (): void => {
    if (!autoScrollEnabled) {
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleUserMessage = (): void => {
    setAutoScrollEnabled(true);
    scrollToBottom();
  };

  const buildFakeMessage = ({
    text,
    role,
    chartData,
    sourceDocuments,
    files,
    citations,
  }: {
    text: any;
    role: any;
    chartData: IStandardChartData | undefined;
    sourceDocuments: any;
    files: any;
    citations: ICitation[];
  }): IMessage => {
    return {
      id: uuidv4(),
      content: text,
      citations: citations,
      files: files,
      isHidden: false,
      chartData: chartData,
      role: role,
      tags: currentTags,
      sourceDocuments: sourceDocuments,
      llmConfig: currentLLMConfig,
      createdAt: Math.floor(Date.now() / 1000),
    };
  };

  const chatWithAi = async () => {
    if (!text || !text.trim()) {
      setText(ConstantValues.EMPTY_STRING);
      return;
    }

    const personalWorkspace: IWorkspace | undefined = workspaces.find(
      (workspace: IWorkspace) =>
        workspace.name === ConstantValues.PERSONAL_WORKSPACE,
    );
    let entityId: string = currentUser.id;
    let prefix: string = "user_store";
    if (
      personalWorkspace &&
      activeWorkspace &&
      activeWorkspace.id !== personalWorkspace.id
    ) {
      entityId = activeWorkspace.id;
      prefix = "workspace";
    }

    const responseNames: IFile[] = await uploadFilesToStorage(
      uploadFiles,
      prefix,
      entityId ?? ConstantValues.EMPTY_STRING,
      true,
    );
    const userMessage = buildFakeMessage({
      text: text,
      role: "user",
      chartData: undefined,
      sourceDocuments: [],
      citations: [],
      files: responseNames,
    });
    let newMessages: IMessage[] = [...temporaryMessages, userMessage];
    setTemporaryMessages(newMessages);
    setText(ConstantValues.EMPTY_STRING);
    setText(ConstantValues.EMPTY_STRING);
    setUploadFiles([]);
    dispatch(setCurrentFiles([]));

    const response = await getAIResponse({
      llmConfig: currentLLMConfig,
      messages: newMessages,
      persistFiles: false,
    });

    if ("error" in response) {
      throw new Error(
        `AI Response Error: ${response.status || "Unknown"} - ${response.message || "Something went wrong"}`,
      );
    }
    // @ts-expect-error -- api.js lacks types; getChunkAndUpdateAIMessage is defined in JS
    const { content, chartData, sourceDocuments, citations } =
      await getChunkAndUpdateAIMessage(
        response,
        response.body,
        null,
        setAiGenerateMessage as any,
        null,
        dispatch,
      );
    if (!content) {
      return;
    }

    const aiMessage: IMessage = buildFakeMessage({
      text: content,
      role: "assistant",
      chartData,
      sourceDocuments,
      files: responseNames,
      citations,
    });
    newMessages = [...newMessages, aiMessage];
    setTemporaryMessages(newMessages);
    return response;
  };

  const chatWithAiMutation = useMutation({
    mutationFn: chatWithAi,
    onSuccess: () => {
      setAiGenerateMessage(ConstantValues.EMPTY_STRING);
      setText(ConstantValues.EMPTY_STRING);
      setUploadFiles([]);
    },
    onError: (error: IAIResponseError) => {
      const errorMessage: string =
        error?.message || "Something went wrong. Please try again later.";
      const errorContent: string = `**Error:** ${errorMessage}\n\nIf this issue persists, please [contact support](mailto:nhhelpu@northhighland.com?subject=AIMS%20Chat%20Error%20Received).`;
      const newAIMessage: IMessage = {
        id: uuidv4(),
        content: errorContent,
        files: [],
        citations: [],
        isHidden: false,
        role: "assistant",
        tags: currentTags,
        sourceDocuments: [],
        llmConfig: currentLLMConfig,
        createdAt: Date.now(),
      };
      setTemporaryMessages([...temporaryMessages, newAIMessage]);
    },
  });

  const copyToClipboard = (message: IMessage) => () => {
    copyTextToClipboard(message?.content ?? ConstantValues.EMPTY_STRING);
    setCopiedMessageId(message?.id ?? ConstantValues.EMPTY_STRING);
  };

  const setEditMessage = (message: IMessage) => (): void => {
    setEditMessageValue(message?.content);
    setEditMessageId(message?.id ?? ConstantValues.EMPTY_STRING);
  };

  const desetEditMessage = () => (): void => {
    setEditMessageValue(null);
    setEditMessageId(null);
  };

  const updateValueOfEditingMessage = (ev: any, data: any): void => {
    setEditMessageValue(data?.value);
  };

  const onClickMessageBar = (): void => {
    chatWithAiMutation.mutate();
  };

  const onEnterPress = async (e: any) => {
    if (e.keyCode === 13 && e.shiftKey === false) {
      e.preventDefault();
      if (chatWithAiMutation.isLoading) return;
      chatWithAi();
    }
  };

  const deselectFile = (file: FileWithId): void => {
    const remainingFiles: FileWithId[] = uploadFiles.filter(
      (f: FileWithId): boolean => f.file.name !== file.file.name,
    );
    setUploadFiles(remainingFiles);
  };

  const resetTemporaryChat = (): void => {
    setText(ConstantValues.EMPTY_STRING);
    setTemporaryMessages([]);
    dispatch(setInitialMessage(null));
  };

  const redoConversationFromEditedMessage =
    (lastMessageRemaining: IMessage) => () => {
      const reaminingMessages = [];
      for (const message of temporaryMessages) {
        if ((message as any)?.id === lastMessageRemaining?.id) {
          break;
        }

        reaminingMessages.push(message);
      }

      lastMessageRemaining.content =
        editMessageValue ?? ConstantValues.EMPTY_STRING;
      reaminingMessages.push(lastMessageRemaining);

      setEditMessageValue(null);
      setEditMessageId(null);
      setCurrentMessages(reaminingMessages);
      chatWithAiMutation.mutate();
    };

  const injectMessageIntoRealConversation = (message: IMessage) => () => {
    setInjectedMessageId(message?.id ?? ConstantValues.EMPTY_STRING);

    if (initialMessage) {
      const updatedMessages = realChatMessages.map((message: IMessage) => {
        if (message?.id === initialMessage?.id) {
          let content: string = ConstantValues.EMPTY_STRING;
          let newEndIndex;

          for (let i = 0; i < message?.content?.length; i++) {
            if (i >= initialMessage?.start && i <= initialMessage?.end) {
              continue;
            }

            if (i === initialMessage?.end + 1) {
              const plainTextContent: string = stripHtml(
                message.content || ConstantValues.EMPTY_STRING,
              );
              const markdownDelimiter: string =
                initialMessage.markdownDelimiter || ConstantValues.EMPTY_STRING;
              const wrappedMessageContent: string = `${markdownDelimiter}${plainTextContent}${markdownDelimiter}`;
              content += wrappedMessageContent;
              newEndIndex = initialMessage?.start + plainTextContent.length;
              continue;
            }

            content += message?.content[i];
          }

          dispatch(
            setInitialMessage({
              ...initialMessage,
              oldEnd: initialMessage.end,
              end: newEndIndex,
            }),
          );
          return { ...message, content };
        }
        return message;
      });

      dispatch(setCurrentMessages(updatedMessages));
      return;
    }

    const messageCopy = {
      ...message,
      conversationId: currentlySelectedConversation?.id,
    };
    const newChatMessages = [...realChatMessages, messageCopy];
    dispatch(setCurrentMessages(newChatMessages));
  };

  function stripHtml(html: any): string {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || ConstantValues.EMPTY_STRING;
  }

  const extractMessageIntoRealConversation =
    (message: IMessage) => async (): Promise<void> => {
      setInjectedMessageId(null);
      if (initialMessage) {
        const reformattedMessages = [];
        for (const m of realChatMessages.slice()) {
          if (m?.id === initialMessage?.id) {
            reformattedMessages.push({
              ...m,
              content: initialMessage?.contentToUpdate,
            });
            continue;
          }

          reformattedMessages.push(m);
        }

        dispatch(
          setInitialMessage({ ...initialMessage, end: initialMessage?.oldEnd }),
        );
        dispatch(setCurrentMessages(reformattedMessages));
        return;
      }

      const realChatCopy: IMessage[] = realChatMessages.slice();
      dispatch(
        setCurrentMessages(
          realChatCopy?.filter((m: IMessage): boolean => m?.id !== message?.id),
        ),
      );
    };

  const saveInjectedMessageToConversation =
    (message: IMessage) => async (): Promise<void> => {
      if (initialMessage) {
        const reformattedMessages: any[] = [];
        for (const m of realChatMessages.slice()) {
          if (m?.id === initialMessage?.id) {
            let content: string = ConstantValues.EMPTY_STRING;
            for (let i = 0; i < m?.content?.length; i++) {
              if (
                m?.content[i] === initialMessage?.markdownDelimiter &&
                i >= initialMessage?.start - 2 &&
                i <= initialMessage?.end + 3
              ) {
                continue;
              }

              content += m?.content[i];
            }
            await updateContentOfConversation({
              conversationId: currentlySelectedConversation?.id,
              messageId: initialMessage?.id,
              content: content,
            });
            reformattedMessages.push({ ...m, content });
            continue;
          }

          reformattedMessages.push(m);
        }

        setCleanUpOnClose(false);
        dispatch(setTemporaryChatOpen(false));
        setTimeout(
          () => dispatch(setCurrentMessages(reformattedMessages)),
          100,
        );
        return;
      }

      const newMessage = await addMessageToConversation({
        message: {
          content: message?.content,
          files: Array.from(uploadFiles || []).map((f: FileWithId) => {
            return { filename: f?.file.name };
          }),
          isHidden: false,
          chartData: message?.chartData,
          role: message?.role,
          tags: currentTags,
          llmConfig: currentLLMConfig,
        },
        conversationId: currentlySelectedConversation?.id,
      });

      const realChatCopy: IMessage[] = realChatMessages.slice();
      const remaining: IMessage[] = realChatCopy?.filter(
        (m: IMessage): boolean => m?.id !== message?.id,
      );
      remaining.push(newMessage);
      dispatch(setCurrentMessages(remaining));
      setInjectedMessageId(null);
    };

  const openImagePreview = (fileUrl: string) => () => {
    setPreviewImage(fileUrl);
  };

  const closeImagePreview = (): void => {
    setPreviewImage(null);
  };

  const handleCloseButton = (): void => {
    dispatch(setTemporaryChatOpen(false));
    dispatch(setTagsForKey({ key: ConstantValues.TEMPORARY_CHAT, tags: [] }));
    dispatch(
      setNewConfigForKey({
        key: ConstantValues.TEMPORARY_CHAT,
        config: { llmType: LLMType.CLAUDE, rag: false, citate: false },
      }),
    );
    dispatch(setInitialMessage(null));
  };

  return (
    <div className={isMobile ? styles.containerMobile : styles.container}>
      {previewImage && (
        <Modal isOpen={!!previewImage} onDismiss={closeImagePreview}>
          <img src={previewImage} alt="Preview" className={styles.modalImage} />
        </Modal>
      )}

      <div className={isMobile ? styles.topBarMobile : styles.topBar}>
        <div
          className={
            isMobile ? styles.topBarSideMobile : styles.topBarRightSection
          }
        >
          <Tooltip content="Erase temporary chat" relationship="label">
            <Button
              appearance="subtle"
              onClick={resetTemporaryChat}
              icon={<Eraser20Regular />}
            />
          </Tooltip>
          <Tooltip content="Close temporary chat" relationship="label">
            <Button
              appearance="subtle"
              onClick={handleCloseButton}
              icon={<Dismiss20Regular />}
            />
          </Tooltip>
        </div>
        <Text
          className={isMobile ? styles.topBarTitleMobile : styles.topBarTitle}
        >
          Temporary Chat
        </Text>
        <div className={isMobile ? styles.topBarSideMobileEnd : undefined}>
          <Button
            appearance="primary"
            icon={<DocumentCopyIcon size={20} />}
            onClick={() => setIsDrawerOpen(true)}
            className={
              isMobile
                ? styles.manageContentButtonMobile
                : styles.manageContentButton
            }
          >
            {!isMobile && <span>Manage Content</span>}
            {currentTags.length > 0 && (
              <span className={styles.badge}>{currentTags.length}</span>
            )}
          </Button>
        </div>
      </div>

      <div
        ref={messagesContainerRef}
        className={isMobile ? styles.messagesAreaMobile : styles.messagesArea}
      >
        {temporaryMessages.length === 0 && (
          <div className={styles.emptyState}>
            <Text className={styles.emptyStateText}>
              Ask me anything about your documents, code, or
              <br />
              projects. I'm here to help!
            </Text>
          </div>
        )}
        {temporaryMessages?.map((message: IMessage, index: number) =>
          message?.id !== editMessageId ? (
            <div
              key={index}
              onMouseEnter={() =>
                setHoveredMessageId(message?.id ?? ConstantValues.EMPTY_STRING)
              }
              onMouseLeave={() => setHoveredMessageId(null)}
              className={
                message?.role === "assistant"
                  ? styles.messageColumnAssistant
                  : styles.messageColumnUser
              }
            >
              <Persona
                name={
                  message?.role === "assistant"
                    ? "NorthHighland AI"
                    : currentUser?.displayName
                }
                secondaryText={message?.role}
                size="large"
                avatar={
                  message?.role === "assistant"
                    ? { image: { src: "/images/logo_sm.png" } }
                    : { name: currentUser?.displayName }
                }
              />
              <Body2
                className={
                  message?.role === "assistant"
                    ? styles.messageBodyAssistant
                    : styles.messageBodyUser
                }
              >
                {renderContentWithCitations(message.content, message.citations)}
              </Body2>
              <div className={styles.flexWrapRow}>
                {message?.tags ? (
                  message?.tags?.map((tag: ITag) => (
                    <TagWithAttachments
                      key={tag.id}
                      sourceKey={ConstantValues.TEMPORARY_CHAT}
                      size="small"
                      entity={{
                        ...tag,
                        id: tag.id || ConstantValues.EMPTY_STRING,
                      }}
                      entityType="tag"
                      secondaryActionFunction={false}
                      isEditable={false}
                      showTagName={true}
                    />
                  ))
                ) : (
                  <Fragment />
                )}
                {message?.llmConfig?.sourceRagCategories?.length > 0 && (
                  <Tag size="small" appearance="outline">
                    {message?.llmConfig?.sourceRagCategories.join(", ")}
                  </Tag>
                )}
                {message?.role === "assistant" && (
                  <Tag size="small">{message?.llmConfig?.llmType}</Tag>
                )}
              </div>

              <div className={styles.filesColumn}>
                {message?.files ? (
                  message?.files?.map((file: IFile, index: number) => (
                    <Card
                      key={index}
                      className={styles.fileCardFit}
                      size="small"
                      role="listitem"
                    >
                      <CardHeader
                        image={{
                          as: "img",
                          src: resolveAsset(
                            decodeURI(
                              file?.filename?.split("/")?.at(-1) ||
                                ConstantValues.EMPTY_STRING,
                            ),
                            file,
                          ),
                          alt: "Word app logo",
                          width: "40px",
                          height: "40px",
                        }}
                        header={
                          <Text size={100} weight="semibold">
                            {decodeURI(
                              file?.filename?.split("/")?.at(-1) ||
                                ConstantValues.EMPTY_STRING,
                            )}
                          </Text>
                        }
                      />
                    </Card>
                  ))
                ) : (
                  <Fragment />
                )}
              </div>
              {!message?.sourceDocuments ? (
                <></>
              ) : message.sourceDocuments.length > 2 ? (
                <>
                  {message.sourceDocuments
                    .slice(0, 2)
                    .map((document: any, index: number) => {
                      const { citationKeyMap } = buildCitationKeyMap(
                        message.citations ?? [],
                      );

                      const matchingCitations =
                        message.citations?.filter(
                          (c: ICitation) => c.file_name === document.filename,
                        ) ?? [];

                      let citationId: number | null = null;
                      for (const citation of matchingCitations) {
                        const key = `${citation.file_name}::${citation.text.trim()}`;
                        const id = citationKeyMap.get(key);
                        if (
                          id !== undefined &&
                          (citationId === null || id < citationId)
                        ) {
                          citationId = id;
                        }
                      }

                      return (
                        <Card key={index} className={styles.sourceDocCard}>
                          <CardHeader
                            image={{
                              as: "img",
                              src: resolveAsset(document?.filename, document),
                              alt: "Document icon",
                              width: "40px",
                              height: "40px",
                            }}
                            header={
                              <div>
                                <Link
                                  onClick={downloadFile(
                                    document.filename ??
                                      ConstantValues.EMPTY_STRING,
                                  )}
                                  title={document?.filename}
                                  className={styles.cursorPointer}
                                >
                                  {document?.filename}
                                </Link>
                                {citationId && (
                                  <Text className={styles.citationText}>
                                    [Citation ID: {citationId}]
                                  </Text>
                                )}
                              </div>
                            }
                          />
                          <p className={styles.sourceDocContent}>
                            {document?.content}
                          </p>
                        </Card>
                      );
                    })}
                  <Accordion collapsible>
                    <AccordionItem value="additionalSourceDocs">
                      <AccordionHeader>
                        +{message.sourceDocuments.length - 2} more
                      </AccordionHeader>
                      <AccordionPanel>
                        {message.sourceDocuments
                          .slice(2)
                          .map((document: any, index: number) => {
                            const { citationKeyMap } = buildCitationKeyMap(
                              message.citations ?? [],
                            );

                            const matchingCitations =
                              message.citations?.filter(
                                (c: ICitation) =>
                                  c.file_name === document.filename,
                              ) ?? [];

                            let citationId: number | null = null;
                            for (const citation of matchingCitations) {
                              const key = `${citation.file_name}::${citation.text.trim()}`;
                              const id = citationKeyMap.get(key);
                              if (
                                id !== undefined &&
                                (citationId === null || id < citationId)
                              ) {
                                citationId = id;
                              }
                            }

                            return (
                              <Card
                                key={index}
                                className={styles.sourceDocCard}
                              >
                                <CardHeader
                                  image={{
                                    as: "img",
                                    src: resolveAsset(
                                      document?.filename,
                                      document,
                                    ),
                                    alt: "Document icon",
                                    width: "40px",
                                    height: "40px",
                                  }}
                                  header={
                                    <div>
                                      <Link
                                        onClick={downloadFile(
                                          document.filename ??
                                            ConstantValues.EMPTY_STRING,
                                        )}
                                        title={document?.filename}
                                        className={styles.cursorPointer}
                                      >
                                        {document?.filename}
                                      </Link>
                                      {citationId && (
                                        <Text className={styles.citationText}>
                                          [Citation ID: {citationId}]
                                        </Text>
                                      )}
                                    </div>
                                  }
                                />
                                <p className={styles.sourceDocContent}>
                                  {document?.content}
                                </p>
                              </Card>
                            );
                          })}
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </>
              ) : (
                <></>
              )}
              <Text italic size={100} className={styles.timestamp}>
                {message.createdAt
                  ? getTimeAndDate(message.createdAt)
                  : undefined}
              </Text>
              {hoveredMessageId === message?.id || isMobile ? (
                <div className={styles.flexWrapRow}>
                  {copiedMessageId === message?.id ? (
                    <Button
                      size="small"
                      icon={<Clipboard16Filled />}
                      onClick={copyToClipboard(message)}
                    >
                      Copied!
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      icon={<Clipboard16Regular />}
                      onClick={copyToClipboard(message)}
                    >
                      Copy
                    </Button>
                  )}

                  {message?.role === "user" ? (
                    <Button
                      onClick={setEditMessage(message)}
                      size="small"
                      icon={<Edit16Regular />}
                    >
                      Edit
                    </Button>
                  ) : (
                    <Fragment />
                  )}

                  {message?.role === "assistant" ? (
                    injectedMessageId !== message?.id ? (
                      <Button
                        onClick={injectMessageIntoRealConversation(message)}
                        size="small"
                        icon={<Edit16Regular />}
                      >
                        Inject
                      </Button>
                    ) : (
                      <div className={styles.flexWrapRow}>
                        <Button
                          onClick={extractMessageIntoRealConversation(message)}
                          size="small"
                          icon={<Edit16Filled />}
                        >
                          Injected
                        </Button>
                        <Button
                          onClick={async () => {
                            await saveInjectedMessageToConversation(message)();
                            dispatch(setInitialMessage(null));
                            dispatch(
                              setCurrentPrompt({
                                key: ConstantValues.TEMPORARY_CHAT,
                                prompt: null,
                              }),
                            );
                          }}
                          appearance="primary"
                          icon={<Checkmark16Regular />}
                          size="small"
                        >
                          Save Injection
                        </Button>
                      </div>
                    )
                  ) : (
                    <Fragment />
                  )}
                </div>
              ) : (
                <div className={styles.hoverPlaceholder} />
              )}
            </div>
          ) : (
            <div
              onMouseEnter={() =>
                setHoveredMessageId(message?.id ?? ConstantValues.EMPTY_STRING)
              }
              onMouseLeave={() => setHoveredMessageId(null)}
              className={
                message?.role === "assistant"
                  ? styles.messageColumnAssistant
                  : styles.messageColumnUser
              }
            >
              <Persona
                name={
                  message?.role === "assistant"
                    ? "NorthHighland AI"
                    : "Kevin Sturgis"
                }
                secondaryText={message?.role}
                size="large"
                avatar={{
                  image: {
                    src: "https://res-1.cdn.office.net/files/fabric-cdn-prod_20230815.002/office-ui-fabric-react-assets/persona-male.png",
                  },
                }}
              />
              <Input
                value={editMessageValue ?? ConstantValues.EMPTY_STRING}
                id={editMessageInputId}
                onChange={updateValueOfEditingMessage}
                className={styles.editInput}
              />

              <div className={styles.editActions}>
                <Button
                  appearance="subtle"
                  onClick={desetEditMessage()}
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  size="small"
                  onClick={redoConversationFromEditedMessage(message)}
                >
                  Save
                </Button>
              </div>
            </div>
          ),
        )}

        <div ref={messagesEndRef} />
      </div>

      <div
        className={
          isMobile ? styles.messageBarSectionMobile : styles.messageBarSection
        }
      >
        <div className={styles.centerContent}>
          <div
            className={
              isMobile ? styles.mainContainerMobile : styles.mainContainer
            }
          >
            <FilePreview
              files={uploadFiles}
              onRemove={deselectFile}
              onPreview={openImagePreview}
            />

            <div className={styles.buttonsContainer}>
              {currentlySelectedConversation == null ||
              currentlySelectedConversation?.userId === currentUser?.id ? (
                <AIModelSettings pageName={ConstantValues.TEMPORARY_CHAT} />
              ) : (
                <></>
              )}
            </div>

            {currentlySelectedConversation == null ||
            currentlySelectedConversation?.userId === currentUser?.id ? (
              <div
                className={
                  isMobile ? styles.fieldContainerMobile : styles.fieldContainer
                }
              >
                <CustomDialog
                  isOpen={!!unsupportedFileMessage}
                  onClose={() => setUnsupportedFileMessage(null)}
                  title="Unsupported File Type"
                >
                  <p>{unsupportedFileMessage}</p>
                </CustomDialog>

                <div className={styles.inputContainer}>
                  <FileUploader
                    onFileSelect={setUploadFiles}
                    setUnsupportedFileMessage={setUnsupportedFileMessage}
                  />

                  <AddPrompt
                    dictKey={ConstantValues.TEMPORARY_CHAT}
                    canManagePrompt={false}
                    data={prompts}
                  />

                  <MessageInput
                    id={ConstantValues.TEMPORARY_CHAT}
                    text={text}
                    setText={setText}
                    setUnsupportedFileMessage={setUnsupportedFileMessage}
                    onKeyPress={onEnterPress}
                    onSend={onClickMessageBar}
                    onAbort={() => {}}
                    disabled={chatWithAiMutation.isLoading}
                    setUploadFiles={setUploadFiles}
                  />
                </div>

                <div className={styles.helperText}>
                  Enter to send, Shift+Enter for new line
                </div>
              </div>
            ) : (
              <Fragment />
            )}
          </div>
        </div>
      </div>

      <Drawer
        type="overlay"
        separator
        position="end"
        open={isDrawerOpen}
        onOpenChange={(_, { open }) => setIsDrawerOpen(open)}
        className={
          isMobile ? styles.drawerMinWidthMobile : styles.drawerMinWidth
        }
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss24Regular />}
                onClick={() => setIsDrawerOpen(false)}
              />
            }
          >
            Knowledge Components
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <div className={styles.drawerContent}>
            <CategoryTagTree
              dictKey={ConstantValues.TEMPORARY_CHAT}
              onDismiss={() => setIsDrawerOpen(false)}
            />
          </div>
        </DrawerBody>
      </Drawer>
    </div>
  );
};
